/**
 * sentry初期処理
 */
import protectedRoutes from '@/routes/protected';
import publicRoutes from '@/routes/public';
import * as Sentry from '@sentry/react';

const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string;
export const VITE_USER_BRANCH = import.meta.env.VITE_USER_BRANCH;
export const FINGERPRINT_APPLICATION_ERROR = 'Application Error';
export const FINGERPRINT_SYSTEM_ERROR = 'System Error';

export const CUSTOM_ERROR_HANDLE = 'Custom Error Handle';

export const createCustomError = (e: Error) => {
  e.name = CUSTOM_ERROR_HANDLE;
  return e;
};

// notMatchCustomErrorMessageはSentryに未出力のエラーであることを判定します
export const notMatchCustomErrorMessage = (name: string) => {
  return !new RegExp(CUSTOM_ERROR_HANDLE).test(name);
};

const getRoutePaths = () => {
  return [...protectedRoutes, ...publicRoutes].filter((value) => value.path !== '/*').map((value) => value.path);
};

const maskItems = ['address', 'familyName', 'firstName', 'familyNameKana', 'firstNameKana', 'email', 'phoneNumber'];

// maskSensitiveDataはJSON文字列からmaskItemsに設定した項目の値をマスクします
const maskSensitiveData = (jsonString: string, keysToMask: string[]): string => {
  try {
    const jsonObject: any = JSON.parse(jsonString);
    return JSON.stringify(maskObject(jsonObject, keysToMask));
  } catch (error) {
    // 失敗した場合は対象のデータを表示しないようにする
    return 'failed to mask the json data';
  }
};

function maskObject(obj: any, keysToMask: string[]): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => maskObject(item, keysToMask));
  }

  const newObj: any = {};
  for (const key in obj) {
    if (keysToMask.includes(key)) {
      newObj[key] = '***';
    } else {
      newObj[key] = maskObject(obj[key], keysToMask);
    }
  }
  return newObj;
}

export const initSentry = () => {
  if (VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: VITE_SENTRY_DSN,
      transport: Sentry.makeFetchTransport,
      stackParser: Sentry.defaultStackParser,
      integrations: Sentry.defaultIntegrations,
      // デバッグを有効化（開発環境のみ）
      debug: VITE_USER_BRANCH === 'devtest',
      // 実行環境
      environment: VITE_USER_BRANCH,
      // スタックトレースを有効化
      attachStacktrace: true,
      // 送信しないエラーメッセージ（正規表現で設定）
      // ignoreErrors: ,
      // 送信しないトランザクション名（正規表現で設定）
      // ignoreTransactions: ,
      // 指定したURLで生成されたエラーメッセージは送信しない（正規表現で設定）
      // denyUrls: ,
      // 指定したURLで生成されたエラーメッセージのみ送信する（正規表現で設定）
      // allowUrls: ,
      // 特定のトランザクションが Sentry に送信される確率（1.0 : 100%）
      tracesSampleRate: 1.0,
      beforeBreadcrumb(breadcrumb, hint) {
        if (!breadcrumb.data) return breadcrumb;

        if (breadcrumb.data['request']) {
          breadcrumb.data['request'] = maskSensitiveData(breadcrumb.data['request'], maskItems);
        }

        if (breadcrumb.data['response']) {
          breadcrumb.data['response'] = maskSensitiveData(breadcrumb.data['response'], maskItems);
        }

        return breadcrumb;
      },
      // イベント送信前のカスタム
      beforeSend(event, hint) {
        // カスタムイベントはイベント送信前の処理をしない
        if (event.extra && event.extra.isCustomEvent) {
          if (event.extra.request) {
            event.extra.request = maskSensitiveData(event.extra.request as string, maskItems);
          }
          return event;
        }
        // 各コンポーネント内の自動生成エラーはpathごとのイシューにまとめる
        const path = event.request?.url ?? window.location.pathname;
        const match = matchPath(path, getRoutePaths());
        if (match) {
          if (event.exception?.values?.length) {
            // Issuerのタイトル名の設定
            event.exception.values[0].type = match.matchedPath;
            event.exception.values[0].value = `${match.matchedPath}: ${event.exception.values[0].value}`;
          }
          // クエリパラメーターの抽出
          const queryParams = pickQueryParams(window.location.search);
          event.message = `${match.matchedPath}: ${event.message}`;
          event.fingerprint = [VITE_USER_BRANCH, match.matchedPath];
          event.extra = {
            parameter: {
              path: match.matchedPath,
              pathParams: JSON.stringify(match.params),
              query: JSON.stringify(queryParams)
            }
          };
        }
        return event;
      }
    });
  }
};

// pickQueryParamsはURLのクエリパラメーターの値をkeyValueの配列の形で抽出します。
export const pickQueryParams = (url: string): Record<string, string>[] => {
  const urlParams = new URLSearchParams(url);
  const queryParams: Record<string, string>[] = [];
  urlParams.forEach((value, key) => {
    queryParams.push({ [key]: value });
  });
  return queryParams;
};

// matchPathは定義されているパスの一覧から対象のパスを探して返します。
// パスパラメーターが使用されている場合は、パスパラメーター部分を実の値から置き換えて対象のパスを返します。
// またパスパラメーターの値もkeyValueの形で返します。
export const matchPath = (
  actualPath: string,
  tagetPathList: string[]
): { matchedPath: string; params: Record<string, string>[] } | null => {
  for (const definedPath of tagetPathList) {
    // パスパラメータを正規表現に変換
    const paramNames: string[] = [];
    const regexPath = definedPath.replace(/:(\w+)/g, (_, paramName) => {
      paramNames.push(paramName);
      return '(\\w+)';
    });
    const regex = new RegExp(`${regexPath}$`);

    // 実際のpathと正規表現をマッチング
    const match = actualPath.match(regex);
    if (match) {
      // パスパラメータの値を取得
      const params = match.slice(1).map((value, index) => {
        const key = paramNames[index];
        return { [key]: value };
      });
      return { matchedPath: definedPath, params: params };
    }
  }
  return null;
};

// setUserはタグとコンテキストに渡されたユーザー情報を埋め込みます
export const setUser = (id: string, name: string, loginId?: string) => {
  Sentry.setUser({
    id: id,
    name: name,
    loginId: loginId
  });
  Sentry.setTags({
    id: id,
    name: name,
    loginId: loginId
  });
  Sentry.setContext('user', {
    id: id,
    name: name,
    loginId: loginId
  });
};

// sendApplicationErrorはアプリケーションレベルのエラーを送信します。
export const sendApplicationError = (error: Error, fingerPrintType: string, extra: Record<string, any>) => {
  Sentry.captureException(error, {
    fingerprint: [VITE_USER_BRANCH, fingerPrintType, FINGERPRINT_APPLICATION_ERROR],
    extra: {
      isCustomEvent: true,
      ...extra
    }
  });
};

// sendApplicationErrorはシステムレベルのエラーを送信します。
export const sendSystemError = (error: Error, fingerPrintType: string, extra: Record<string, any>) => {
  Sentry.captureException(error, {
    fingerprint: [VITE_USER_BRANCH, fingerPrintType, FINGERPRINT_SYSTEM_ERROR],
    extra: {
      isCustomEvent: true,
      ...extra
    }
  });
};
